// Canonical frontend analytics emitter: the single client-side path into the
// BigQuery event stream (analytics/ANALYTICS_BQ_OVERHAUL_PHASE0.md Part D).
//
// - Every event is validated against the registry before it leaves the app;
//   invalid events are dropped with a console warning, never thrown.
// - Events batch in memory and flush to the log_analytics_events callable
//   (every FLUSH_INTERVAL_MS, when the batch is full, or when the tab hides).
// - Failed flushes requeue once capacity allows; analytics loss is acceptable,
//   breaking product flows is not.

import { httpsCallable } from 'firebase/functions';
import { functions, app } from '../firebase';
import { validateEventPayload, resolveProduct } from './eventRegistry';
import { getAnalyticsSessionId, claimSessionStart, releaseSessionStartClaim } from './session';
import { getAnalyticsDeviceFields, getAnalyticsSessionContext } from './deviceFields';

// Emission is enabled in every environment: production capture was approved
// by the founder on 2026-08-04 (analytics/ANALYTICS_BQ_OVERHAUL_PHASE0.md Part C5
// records the decision; see also PR #259). The gate is retained as a
// fail-safe — an unknown or missing Firebase project emits nothing — and as
// the single switch-off point should collection ever need to pause. The
// server keeps its own independent kill-switch (_PROD_DELIVERY_ENABLED in
// event_ingest.py).
export function analyticsEventsEnabled() {
  return Boolean(app?.options?.projectId);
}

const FLUSH_INTERVAL_MS = 10000;
const MAX_BATCH = 20;
const MAX_QUEUE = 200;

let queue = [];
let flushTimer = null;
let listenerBound = false;

const newInsertId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Final-flush caveat: on a real tab close the browser may cancel this async
// callable request, so the last ~10s of a session are the events most likely
// to be lost — which can slightly bias funnel/drop-off metrics. Accepted for
// now: navigator.sendBeacon would be more durable but cannot carry the
// Authorization header the callable requires; a beacon-friendly endpoint is
// a possible Phase 2 refinement if the loss shows up in the data.
const bindLifecycleFlush = () => {
  if (listenerBound || typeof document === 'undefined') return;
  listenerBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAnalyticsEvents();
  });
};

const scheduleFlush = () => {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushAnalyticsEvents();
  }, FLUSH_INTERVAL_MS);
};

let flushing = false;

const SESSION_STARTED_EVENT = 'session_started';

// Build identity, so a regression can be traced to the release that shipped it
// — the stale-bundle hazard makes "which build produced this row" a real
// question. Supplied by CI as VITE_APP_VERSION; until it is, the field is
// simply absent, which is honest: those rows predate the capture and need no
// backfill. Read through the same import.meta.env pattern as the Firebase
// config, so the existing Jest transform handles it.
const APP_VERSION = (() => {
  const raw = import.meta.env.VITE_APP_VERSION;
  return typeof raw === 'string' && raw ? raw.slice(0, 64) : undefined;
})();

// Device, locale/timezone and accessibility context for this sitting. Emitted
// at most once per session (the claim is stored beside the session ID, so a
// reload does not produce a second one); everything else joins to it on
// session_id rather than repeating the block on every payload.
// The claim is only spent once the event is actually queued: a start that never
// made it into the queue (a full queue, say) would otherwise leave the session
// without its context row for the life of the tab.
function emitSessionStartedOnce() {
  if (!claimSessionStart()) return;
  const queued = emitAnalyticsEvent(SESSION_STARTED_EVENT, {
    ...getAnalyticsDeviceFields(),
    ...getAnalyticsSessionContext(),
  });
  if (!queued) releaseSessionStartClaim();
}

const carriesSessionStart = (batch) =>
  batch.some((event) => event.event_name === SESSION_STARTED_EVENT);

export async function flushAnalyticsEvents() {
  if (queue.length === 0) return;
  // The synchronous queue slice below already prevents double-sends, but the
  // guard makes the intent explicit: overlapping triggers (batch-full,
  // timer, visibilitychange) collapse into one in-flight flush, and the
  // pending timer is cleared so it cannot fire mid-flush.
  if (flushing) return;
  flushing = true;
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);
  try {
    const logEvents = httpsCallable(functions, 'log_analytics_events');
    await logEvents({ events: batch });
  } catch (error) {
    // Some failures are permanent, and retrying them is worse than losing the
    // events. The ingest requires a signed-in caller, so anything emitted
    // before sign-in (login attempts and failures, the public referral funnel)
    // is rejected every time; re-queueing it would retry every 10s for the
    // life of the page and still never land. Drop those, retry the rest.
    const permanent =
      error?.code === 'functions/unauthenticated' ||
      error?.code === 'unauthenticated' ||
      error?.code === 'functions/permission-denied';
    if (!permanent && queue.length + batch.length <= MAX_QUEUE) {
      queue = batch.concat(queue);
    } else if (carriesSessionStart(batch)) {
      // This batch is gone for good, and with it the session's only context
      // row. Re-arm the once-per-session claim so the next event re-attempts
      // it under the same session_id; delivered starts are still at-most-once.
      releaseSessionStartClaim();
    }
  } finally {
    flushing = false;
  }
  if (queue.length > 0) scheduleFlush();
}

export function emitAnalyticsEvent(
  eventName,
  payload = {},
  { sessionId, product, eventVersion } = {},
) {
  if (!analyticsEventsEnabled()) return false;
  // The session's context block is emitted once, riding the first event of the
  // session rather than a mount hook: no component owns "a session began", and
  // this way the context exists for any surface that emits at all. Queued
  // before the triggering event so the context precedes what it describes.
  if (eventName !== SESSION_STARTED_EVENT) emitSessionStartedOnce();
  const errors = validateEventPayload(eventName, payload);
  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[analytics] dropped ${eventName}:`, errors);
    return false;
  }
  if (queue.length >= MAX_QUEUE) return false;

  queue.push({
    event_name: eventName,
    // Per-event version, not a constant: a semantic revision to an existing
    // event (registry rule R3) bumps it at the emit site so analysts can
    // separate the eras instead of guessing by date.
    event_version: Number.isInteger(eventVersion) && eventVersion > 0 ? eventVersion : 1,
    occurred_at: new Date().toISOString(),
    insert_id: newInsertId(),
    // Anonymous per-tab session for sitting-level analysis; an explicit
    // sessionId (e.g. WLC's own funnel session) takes precedence.
    session_id: sessionId || getAnalyticsSessionId(),
    product: resolveProduct(eventName, product),
    ...(APP_VERSION ? { app_version: APP_VERSION } : {}),
    payload,
  });
  bindLifecycleFlush();
  if (queue.length >= MAX_BATCH) {
    flushAnalyticsEvents();
  } else {
    scheduleFlush();
  }
  return true;
}

// Test-only: reset module state between tests.
export function __resetAnalyticsQueueForTests() {
  queue = [];
  flushing = false;
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}
